/**
 * One row of the reasoning trail — and, when the backend attached a `detail`,
 * the thing that opens (FS-659).
 *
 * A step without `detail` renders exactly as it always has: an icon, a number
 * and a label, inert. That is the legacy kg-agent / Python shape, whose labels
 * ("Searching the knowledge base…") describe a phase rather than a call and have
 * nothing behind them to show. Only a step that carries what it actually did
 * becomes a button.
 *
 * Shared by both ReasoningPath variants (V2 chrome and the legacy card) so the
 * two can't drift into different ideas of what opening a step shows.
 */
import React, { useState } from 'react'
import { AlertCircle, CheckCircle, ChevronDown, ChevronRight, Circle, ExternalLink } from 'lucide-react'
import type { ReasoningStep } from '@/service/api'
import { colors } from '@/styles/colors'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'

interface ReasoningStepRowProps {
  step: ReasoningStep
  /** Path-level status, so an in-flight step can pulse. */
  status: string
}

/** `1.2s` / `840 ms`. Sub-second steps read wrong as `0.8s`. */
function formatStepDuration(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)}s`
}

function StepIcon({ step, status }: { step: ReasoningStep; status: string }) {
  // An `error`/`empty` step that "completed" is still not a success — a green
  // check on a step that found nothing is the kind of quiet over-claim this
  // whole surface exists to remove.
  if (step.detail?.outcome === 'error') {
    return <AlertCircle size={16} style={{ color: colors.gray[600] }} aria-hidden="true" />
  }
  if (step.detail?.outcome === 'empty') {
    return <Circle size={16} className="text-gray-400 dark:text-gray-500" aria-hidden="true" />
  }
  if (step.completed) {
    return <CheckCircle size={16} className="text-brand dark:text-brand-light" aria-hidden="true" />
  }
  return (
    <Circle
      size={16}
      className={cn(
        'text-gray-400 dark:text-gray-500',
        status === 'in_progress' && 'text-brand dark:text-brand-light animate-pulse'
      )}
      aria-hidden="true"
    />
  )
}

const ReasoningStepRow: React.FC<ReasoningStepRowProps> = ({ step, status }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const detail = step.detail
  const panelId = `reasoning-step-panel-${step.step}`

  const header = (
    <>
      <div className="flex items-center justify-center flex-shrink-0">
        <StepIcon step={step} status={status} />
      </div>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{step.step}.</span>
      <span
        className={cn(
          'text-sm text-left',
          step.completed ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'
        )}
      >
        {step.description}
      </span>
    </>
  )

  if (!detail) {
    return (
      <div className="flex items-center gap-3" data-testid={`reasoning-step-${step.step}`}>
        {header}
      </div>
    )
  }

  return (
    <div data-testid={`reasoning-step-${step.step}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="w-full flex items-center gap-3 text-left hover:opacity-80 transition-opacity"
      >
        {header}
        <span className="ml-auto flex items-center gap-2 text-xs text-gray-500 dark:text-dark-text-muted">
          {typeof step.durationMs === 'number' && <span>{formatStepDuration(step.durationMs)}</span>}
          {isOpen ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
        </span>
      </button>

      {isOpen && (
        <div
          id={panelId}
          className="ml-7 mt-1 space-y-2 rounded-md border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg p-2"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400">{detail.summary}</p>

          {detail.preview && (
            <p className="text-xs italic text-gray-500 dark:text-dark-text-muted whitespace-pre-wrap break-words">
              {detail.preview}
            </p>
          )}

          {detail.sources && detail.sources.length > 0 && (
            <div className="space-y-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                {t('chat:messages.reasoning_sources', { defaultValue: 'Sources' })}
              </p>
              {detail.sources.map((source, i) => (
                <div key={source.source_id ?? `${step.step}-${i}`} className="text-xs">
                  {source.url ? (
                    // New tab, matching `SourcesList`: a `kind:'web'` source is an
                    // absolute external URL, and following it in place would drop
                    // the user out of the conversation they were auditing.
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand dark:text-brand-light hover:underline break-all"
                    >
                      {source.title ?? source.url}
                      <ExternalLink size={11} className="flex-shrink-0" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="text-gray-600 dark:text-gray-400 break-all">
                      {source.title}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ReasoningStepRow
